import clsx from 'clsx'
import type { PropsWithChildren, ReactNode } from 'react'
import React from 'react'
import type { AriaLiveRole } from '../utilities/aria'

interface BaseBadgeProps {
  /**
   * Accessible label for the badge
   */
  'aria-label'?: string
  /**
   * The color scheme of the badge
   * @default 'default'
   */
  color?: BadgeColor
  /**
   * If true, the badge will be hidden, both visually and from screen readers
   * @default false
   */
  invisible?: boolean
  /**
   * Maximum value to display. If the value exceeds this, it will show {number}+ (e.g. 99+)
   * @default 99
   */
  max?: number
  /**
   * The shape of the anchor element the badge overlaps
   * @default 'rectangle'
   */
  overlap?: BadgeOverlap
  /**
   * The position of the badge relative to its anchor element
   * @default { x: 'right', y: 'top' }
   */
  position?: { x?: BadgeXPosition; y?: BadgeYPosition }
  /**
   * [ARIA live region](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Guides/Live_regions) role for accessibility.
   * Use `alert` for important, time-sensitive information, or `status` for advisory information.
   */
  role?: AriaLiveRole.Alert | AriaLiveRole.Status
  /**
   * If true, the badge will be visible when value is zero
   * @default false
   */
  showZero?: boolean
  /**
   * The content value to display in the badge
   */
  value?: ReactNode
  /**
   * The shape variant of the badge
   * @default 'default'
   */
  variant?: BadgeVariant
}

type PresentationBadgeProps = {
  'aria-label'?: never
  role: AriaLiveRole.Presentation
} & Omit<BaseBadgeProps, 'aria-label' | 'role'>

export type BadgeProps = BaseBadgeProps | PresentationBadgeProps

type BadgeColor = 'default' | 'error' | 'primary' | 'secondary'

type BadgeColors = {
  [key in BadgeColor]: string
}

type BadgeXPosition = 'left' | 'right'

type BadgeYPosition = 'top' | 'bottom'

type BadgePositions = {
  [key in BadgeOverlap]: { [key in BadgeXPosition | BadgeYPosition]: string }
}

type BadgeOverlap = 'circle' | 'rectangle'

type BadgeVariant = 'default' | 'dot'

type BadgeVariants = {
  [key in BadgeVariant]: string
}

const colors: BadgeColors = {
  default: `bg-light-grey-main text-black-90`,
  error: `bg-error-main text-white-100`,
  primary: `bg-primary-main text-white-100`,
  secondary: `bg-secondary-main text-white-100`,
}

const positions: BadgePositions = {
  rectangle: {
    top: 'top-0 -translate-y-1/2',
    bottom: 'bottom-0 translate-y-1/2',
    left: 'left-0 -translate-x-1/2',
    right: 'right-0 translate-x-1/2',
  },
  circle: {
    top: 'top-7/50 -translate-y-1/2',
    bottom: 'bottom-7/50 translate-y-1/2',
    left: 'left-7/50 -translate-x-1/2',
    right: 'right-7/50 translate-x-1/2',
  },
}

const variants: BadgeVariants = {
  default:
    'h-5 min-w-5 justify-center items-center content-center px-1.5 text-xs leading-6',
  dot: 'h-2 w-2',
}

function Badge({
  'aria-label': label,
  children,
  color = 'default',
  value = null,
  invisible = false,
  max = 99,
  overlap = 'rectangle',
  role,
  showZero = false,
  variant = 'default',
  position = { x: 'right', y: 'top' },
}: PropsWithChildren<BadgeProps>): JSX.Element {
  let isVisible = !invisible

  if ((value === 0 || value === '0') && !showZero) {
    isVisible = false
  }

  const isNumeric =
    typeof value === 'number' || (typeof value === 'string' && !isNaN(+value))

  if (value && isNumeric && +value > max) {
    value = `${max}+`
  }

  return (
    <div>
      <span
        aria-label={label}
        className="relative inline-flex shrink-0 align-middle"
        role={role}
      >
        {children}
        <span
          aria-hidden={!isVisible}
          className={clsx(
            'absolute z-10 flex font-sans transition-all duration-200 ease-in-out rounded-full',
            colors[color],
            isVisible ? 'scale-100' : 'scale-0',
            positions[overlap][position.x || 'right'],
            positions[overlap][position.y || 'top'],
            variants[variant],
          )}
        >
          {variant === 'default' ? value : null}
        </span>
      </span>
    </div>
  )
}

export default Badge
